import React, { useState, useEffect } from "react";
import api from "@/components/customAxios/Axios";
import { useToast } from "../toast";
import { IoClose } from "react-icons/io5";
import { useLoader } from "@/context/loaderContext";

import CandidateTotalScore from "./CandidateTotalScore";
import CandidateBehaviouralScore from "./CandidateBehaviouralScore";
import CandidateTechnicalScore from "./CandidateTechnicalScore";
import CandidateCodingScore from "./CandidateCodingScore";
interface InterviewDataProps {
	onClick: () => void;
	resume_id: string;
	jobId: string;
}

const CandidateResult: React.FC<InterviewDataProps> = ({
	onClick,
	resume_id,
	jobId,
}) => {
	const { setLoading } = useLoader();
	const [resultData, setResultData] = useState<any>(null);
	const [activeSection, setActiveSection] = useState("Total Score");
	const toast = useToast();

	const getCandidateResult = async () => {
		setLoading(true);

		try {
			const resp = await api.post("/fetch_user_responses", {
				resume_id,
				job_id: jobId,
			});
			setResultData(resp.data);
		} catch (err: any) {
			setLoading(false);
			toast.error({
				duration: 5000,
				status: "error",
				title: "Error Fetching Results",
				description: err.response?.data?.error || err.response?.data?.message || "Failed to fetch candidate results. Please try again.",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};
	const downloadResult = async () => {
		setLoading(true);

		try {
			const resp = await api.post("/download_candidate_pdf", {
				resume_id,
				job_id: jobId,
			});
			if (resp.status === 200) {
				const link = document.createElement("a");
				link.href = resp.data.s3_link;
				link.setAttribute("download", ""); // Optional: browser will use filename from server
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		} catch (err: any) {
			toast.error({
				duration: 5000,
				status: "error",
				title: "Error Downloading Results",
				description: err.response?.data?.error || err.response?.data?.message || "Failed to download candidate results. Please try again.",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getCandidateResult();
	}, []);

	if (!resultData) {
		return <></>;
	}
	return (
		<div className="">
			<div
				className={` fixed inset-0  flex justify-center items-center z-50`}
			>
				<div className="relative bg-white dark:bg-zinc-800 rounded-xl shadow-lg p-6 w-[700px] max-w-4xl h-[95vh] overflow-y-auto">
					{/* Close Button */}

					<div className="flex justify-end">
						<button
							onClick={onClick}
							className="p-2 rounded-full bg-red-500 text-white font-medium shadow hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-red-300 transition-transform transform hover:scale-110"
							aria-label="Close Modal"
						>
							<IoClose size={20} />
						</button>
					</div>
					{/* Header */}
					<div className="flex   gap-3 ">
						<img
							src={resultData?.candidate_image}
							alt="Candidate"
							className="w-20 h-20 rounded-full shadow-md mb-4"
						/>
						<h1 className="text-2xl mt-2 font-bold text-gray-800 dark:text-white text-center font-heading">
							Interview Data for {resultData?.candidate_name}
							{/* Download Result */}
							<div className="flex items-center my-2 font-subheading">
								<button
									onClick={downloadResult}
									className="flex items-center space-x-2 text-blue-600 hover:text-blue-500"
								>
									<svg
										width="29"
										height="24"
										viewBox="0 0 29 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g clipPath="url(#clip0_77_1708)">
											<path
												d="M9.66699 18L14.5003 22.8M14.5003 22.8L19.3337 18M14.5003 22.8V12"
												stroke="#3770ED"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
											<path
												d="M25.2307 19.308C26.2813 18.5744 27.0691 17.5274 27.4798 16.319C27.8905 15.1106 27.9029 13.8036 27.515 12.5878C27.1271 11.372 26.3592 10.3105 25.3227 9.55749C24.2862 8.80446 23.035 8.39902 21.7507 8.40002H20.2282C19.8648 6.99346 19.1848 5.68711 18.2395 4.57931C17.2941 3.47151 16.108 2.59113 14.7705 2.00444C13.4329 1.41775 11.9788 1.14005 10.5175 1.19224C9.05625 1.24442 7.62595 1.62514 6.33429 2.30573C5.04263 2.98632 3.92326 3.94904 3.06047 5.12141C2.19767 6.29379 1.61393 7.64527 1.35318 9.07412C1.09244 10.503 1.16148 11.9719 1.55512 13.3704C1.94876 14.769 2.65673 16.0606 3.62574 17.148"
												stroke="#3770ED"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</g>
										<defs>
											<clipPath id="clip0_77_1708">
												<rect width="29" height="24" fill="white" />
											</clipPath>
										</defs>
									</svg>

									<span className="text-sm font-medium mt-2">
										Download Result
									</span>
								</button>
							</div>
						</h1>
					</div>

					{/* Navbar */}
					<div className="flex justify-center flex-wrap gap-3 mb-6 mt-3 font-subheading">
						{["Total Score", "Behavioral", "Technical", "Coding"].map(
							(section) => {
								if (
									section === "Coding" &&
									!resultData?.code_response.coding_evaluation
								)
									return;

								return (
									<button
										key={section}
										className={`px-4 py-2 rounded-lg text-sm font-medium transition duration-300 focus:outline-none text-[#438437] ${activeSection === section
											? "bg-[#BDE4C3]"
											: "border border-[#BDE4C3] "
											}`}
										onClick={() => setActiveSection(section)}
									>
										{section}
									</button>
								);
							}
						)}
					</div>

					{/* Section Content */}
					<div className="space-y-4">
						{activeSection === "Total Score" && (
							<CandidateTotalScore resultData={resultData} />
						)}

						{activeSection === "Behavioral" && (
							<CandidateBehaviouralScore
								transcripts={resultData?.audio_transcript}
							></CandidateBehaviouralScore>
						)}

						{activeSection === "Technical" && (
							<CandidateTechnicalScore
								techResponses={resultData?.tech_response}
							/>
						)}

						{activeSection === "Coding" && (
							<section>
								<CandidateCodingScore
									codeResponses={resultData?.code_response}
								/>
							</section>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default CandidateResult;
